import {
  ArchiveIcon,
  CheckIcon,
  LayersIcon,
  MoreHorizontalIcon,
  PauseIcon,
  PencilIcon,
  PlayIcon,
  PlusIcon,
  RefreshCwIcon,
  ShieldIcon,
  UsersIcon,
} from 'lucide-react'
import { ApiErrorText } from '@/components/UpgradePrompt'
import { useState, type FormEvent } from 'react'
import { Link } from 'react-router'

import { ApiError, errorMessage } from '@/api/client'
import {
  useCreateZone,
  useMe,
  useUpdateZone,
  useUsers,
  useZoneLifecycle,
  useZones,
  type ZoneLifecycleAction,
} from '@/api/queries'
import type { AccountUser, ZoneStateFilter, ZoneSummary } from '@/api/types'
import { PageBody, PageHeader } from '@/components/AppShell'
import { LifecycleBadge, RoleBadge } from '@/components/badges'
import { CopyableValue } from '@/components/CodeBlock'
import { EmptyState, ErrorState, TableSkeleton } from '@/components/states'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import { Badge } from '@/components/ui/badge'
import { Button, buttonVariants } from '@/components/ui/button'
import { Card } from '@/components/ui/card'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { formatAbsolute, formatRelative } from '@/lib/format'
import { cn } from '@/lib/utils'
import { useActiveZone } from '@/zone/useActiveZone'

import { zoneTimerPatch } from './zone-timers'
import { useReadOnly } from '@/staff/hooks'

/* -------------------------------------------------------------------------- */
/* Create                                                                     */
/* -------------------------------------------------------------------------- */

function CreateZoneDialog() {
  const create = useCreateZone()
  const { selectZone } = useActiveZone()
  const [open, setOpen] = useState(false)
  const [name, setName] = useState('')
  const [description, setDescription] = useState('')

  function submit(event: FormEvent) {
    event.preventDefault()
    const trimmed = name.trim()
    if (trimmed.length === 0) return
    const body = description.trim().length > 0 ? { name: trimmed, description: description.trim() } : { name: trimmed }
    create.mutate(body, {
      onSuccess: (zone) => {
        selectZone(zone.id)
        setOpen(false)
        setName('')
        setDescription('')
      },
    })
  }

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        setOpen(next)
        if (!next) create.reset()
      }}
    >
      <Button
        onClick={() => {
          setOpen(true)
        }}
      >
        <PlusIcon className="size-4" />
        New zone
      </Button>
      <DialogContent>
        <form onSubmit={submit}>
          <DialogHeader>
            <DialogTitle>Create a zone</DialogTitle>
            <DialogDescription>
              A zone is its own isolation boundary: its own MCP URL, its own connections, its
              own channels, its own message history. Nothing crosses between zones.
            </DialogDescription>
          </DialogHeader>

          <div className="min-h-0 flex-1 space-y-4 overflow-y-auto py-5">
            <div className="space-y-1.5">
              <Label htmlFor="zone-name">Name</Label>
              <Input
                id="zone-name"
                value={name}
                onChange={(event) => {
                  setName(event.target.value)
                }}
                placeholder="checkout-rewrite"
                className="font-mono"
                autoComplete="off"
                autoFocus
              />
              <p className="text-xs text-muted-foreground">
                Unique within your account. It also becomes the default MCP server name in the
                generated <code className="font-mono">.mcp.json</code>.
              </p>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="zone-description">Description (optional)</Label>
              <Input
                id="zone-description"
                value={description}
                onChange={(event) => {
                  setDescription(event.target.value)
                }}
                placeholder="What happens in this zone"
                autoComplete="off"
              />
            </div>
            {create.isError ? (
              <Alert variant="destructive">
                <AlertTitle>Could not create the zone</AlertTitle>
                <AlertDescription><ApiErrorText error={create.error} /></AlertDescription>
              </Alert>
            ) : null}
          </div>

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => {
                setOpen(false)
              }}
              disabled={create.isPending}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={create.isPending || name.trim().length === 0}>
              {create.isPending ? 'Creating…' : 'Create zone'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}

/* -------------------------------------------------------------------------- */
/* Rename / describe                                                          */
/* -------------------------------------------------------------------------- */

/** The RFC 9457 title of a refused save, when the API gave one — e.g. "Poll cadence out of range". */
function problemTitle(error: unknown): string | undefined {
  return error instanceof ApiError ? error.problem?.title : undefined
}

/** WI6 bounds, as stated by the server. Repeated here only for the inputs' `min`/`max`. */
const POLL_MIN_SECONDS = 5
const POLL_MAX_SECONDS = 3600

/** CB-WI3 bounds, likewise: the server refuses anything else and its problem title is shown. */
const OFFLINE_AFTER_MIN_SECONDS = 60
const OFFLINE_AFTER_MAX_SECONDS = 86400
const OFFLINE_RELEASE_MIN_DAYS = 0
const OFFLINE_RELEASE_MAX_DAYS = 365
// SECURE_FILES decision 5: the same bounds as the account's secret lifetime, deliberately, so an
// administrator meets one rule and not two. The server enforces them; these are the affordance.
const SECURE_FILE_TTL_MIN_SECONDS = 60
const SECURE_FILE_TTL_MAX_SECONDS = 86400

function EditZoneDialog({
  zone,
  onClose,
}: {
  zone: ZoneSummary | undefined
  onClose: () => void
}) {
  const update = useUpdateZone()
  const [name, setName] = useState('')
  const [description, setDescription] = useState('')
  const [pollIdle, setPollIdle] = useState('')
  const [pollActive, setPollActive] = useState('')
  const [offlineAfter, setOfflineAfter] = useState('')
  const [offlineReleaseDays, setOfflineReleaseDays] = useState('')
  const [secureFileTtl, setSecureFileTtl] = useState('')
  const [loadedFor, setLoadedFor] = useState<string | undefined>(undefined)

  // Seed the form the first time this dialog is opened for a given zone.
  if (zone !== undefined && loadedFor !== zone.id) {
    setLoadedFor(zone.id)
    setName(zone.name)
    setDescription(zone.description ?? '')
    setPollIdle(String(zone.poll_idle_seconds))
    setPollActive(String(zone.poll_active_seconds))
    setOfflineAfter(String(zone.offline_after_seconds))
    setOfflineReleaseDays(String(zone.offline_release_days))
    setSecureFileTtl(String(zone.secure_file_ttl_seconds))
  }

  /**
   * The dialog stays mounted after it closes, so the seed marker has to be
   * cleared here. Leaving it set means reopening the same zone skips the
   * seeding branch and re-presents the *abandoned* edit as though it were the
   * stored value — one more click and a half-typed name is saved.
   */
  function close() {
    setLoadedFor(undefined)
    update.reset()
    onClose()
  }

  function submit(event: FormEvent) {
    event.preventDefault()
    if (zone === undefined) return
    update.mutate(
      {
        zoneId: zone.id,
        body: {
          name: name.trim(),
          description: description.trim(),
          // WI6 + CB-WI3. Each field is sent whenever it parses; the server owns the bounds and
          // answers a 400 titled "Poll cadence out of range" or "Offline timers out of range" that
          // the alert below shows verbatim. Only the per-field range is repeated client-side (the
          // inputs' min/max), never active ≤ idle. See zone-timers.ts for why an empty box is
          // omitted rather than sent as 0 — for offline_release_days, 0 means "never".
          ...zoneTimerPatch({
            pollIdle,
            pollActive,
            offlineAfter,
            offlineReleaseDays,
            secureFileTtl,
          }),
        },
      },
      { onSuccess: close },
    )
  }

  // The problem title is the headline when there is one: "Poll cadence out of range" says more
  // than "Could not save the zone". The detail goes underneath unless it is the same sentence.
  const failureTitle = update.isError
    ? (problemTitle(update.error) ?? 'Could not save the zone')
    : undefined
  const failureDetail = update.isError ? errorMessage(update.error) : undefined

  return (
    <Dialog
      open={zone !== undefined}
      onOpenChange={(next) => {
        if (!next) close()
      }}
    >
      {/*
        * The field list grew a fifth number with SECURE_FILES, and on a short viewport that pushed
        * the footer off the bottom: the dialog has no scroll container of its own, so Save and
        * Cancel simply became unreachable (caught by identity.spec.ts, which could no longer press
        * Cancel). The fields scroll, the header and footer stay put.
        */}
      <DialogContent className="max-h-[90dvh] overflow-hidden">
        <form onSubmit={submit} className="flex max-h-[85dvh] flex-col">
          <DialogHeader>
            <DialogTitle>Edit zone</DialogTitle>
            <DialogDescription>
              Renaming is safe: the MCP URL is built from the zone&apos;s{' '}
              <span className="font-mono">public_id</span>, which never changes. Clients keep
              working. The poll cadence is what every agent in the zone is told to follow.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-5">
            <div className="space-y-1.5">
              <Label htmlFor="edit-zone-name">Name</Label>
              <Input
                id="edit-zone-name"
                value={name}
                onChange={(event) => {
                  setName(event.target.value)
                }}
                className="font-mono"
                autoComplete="off"
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="edit-zone-description">Description</Label>
              <Input
                id="edit-zone-description"
                value={description}
                onChange={(event) => {
                  setDescription(event.target.value)
                }}
                autoComplete="off"
              />
            </div>
            <div className="grid gap-4 sm:grid-cols-2">
              <div className="space-y-1.5">
                <Label htmlFor="edit-zone-poll-idle">Idle poll interval (s)</Label>
                <Input
                  id="edit-zone-poll-idle"
                  type="number"
                  inputMode="numeric"
                  min={POLL_MIN_SECONDS}
                  max={POLL_MAX_SECONDS}
                  step={1}
                  required
                  value={pollIdle}
                  onChange={(event) => {
                    setPollIdle(event.target.value)
                  }}
                  className="font-mono"
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="edit-zone-poll-active">Active poll interval (s)</Label>
                <Input
                  id="edit-zone-poll-active"
                  type="number"
                  inputMode="numeric"
                  min={POLL_MIN_SECONDS}
                  max={POLL_MAX_SECONDS}
                  step={1}
                  required
                  value={pollActive}
                  onChange={(event) => {
                    setPollActive(event.target.value)
                  }}
                  className="font-mono"
                />
              </div>
            </div>
            <p className="text-xs text-pretty text-muted-foreground">
              How often an agent checks for messages: <em>idle</em> between conversations,{' '}
              <em>active</em> while one is going. Both between {POLL_MIN_SECONDS} and{' '}
              {POLL_MAX_SECONDS} seconds, and active never longer than idle. The fields above hold
              this zone&apos;s current values. Agents pick a change up on their next{' '}
              <span className="font-mono">connect</span> or{' '}
              <span className="font-mono">refresh</span>.
            </p>
            <div className="grid gap-4 sm:grid-cols-2">
              <div className="space-y-1.5">
                <Label htmlFor="edit-zone-offline-after">Offline after (seconds)</Label>
                <Input
                  id="edit-zone-offline-after"
                  type="number"
                  inputMode="numeric"
                  min={OFFLINE_AFTER_MIN_SECONDS}
                  max={OFFLINE_AFTER_MAX_SECONDS}
                  step={1}
                  required
                  value={offlineAfter}
                  onChange={(event) => {
                    setOfflineAfter(event.target.value)
                  }}
                  className="font-mono"
                />
                <p className="text-xs text-pretty text-muted-foreground">
                  an agent silent this long shows as offline; nothing is lost
                </p>
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="edit-zone-offline-release">Auto-release after (days)</Label>
                <Input
                  id="edit-zone-offline-release"
                  type="number"
                  inputMode="numeric"
                  min={OFFLINE_RELEASE_MIN_DAYS}
                  max={OFFLINE_RELEASE_MAX_DAYS}
                  step={1}
                  required
                  value={offlineReleaseDays}
                  onChange={(event) => {
                    setOfflineReleaseDays(event.target.value)
                  }}
                  className="font-mono"
                />
                <p className="text-xs text-pretty text-muted-foreground">
                  an offline identity is released after this many days; 0 = never; each sender is
                  notified
                </p>
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="edit-zone-secure-ttl">Secure file lifetime (seconds)</Label>
                <Input
                  id="edit-zone-secure-ttl"
                  type="number"
                  inputMode="numeric"
                  min={SECURE_FILE_TTL_MIN_SECONDS}
                  max={SECURE_FILE_TTL_MAX_SECONDS}
                  step={1}
                  required
                  value={secureFileTtl}
                  onChange={(event) => {
                    setSecureFileTtl(event.target.value)
                  }}
                  className="font-mono"
                />
                <p className="text-xs text-pretty text-muted-foreground">
                  unclaimed secure files are destroyed when this elapses, and the sender is told;
                  the clock starts when the bytes are stored
                </p>
              </div>
            </div>
            {failureTitle === undefined ? null : (
              <Alert variant="destructive">
                <AlertTitle>{failureTitle}</AlertTitle>
                {failureDetail === undefined || failureDetail === failureTitle ? null : (
                  <AlertDescription>{failureDetail}</AlertDescription>
                )}
              </Alert>
            )}
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={close} disabled={update.isPending}>
              Cancel
            </Button>
            <Button type="submit" disabled={update.isPending || name.trim().length === 0}>
              {update.isPending ? 'Saving…' : 'Save changes'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}

/* -------------------------------------------------------------------------- */
/* Zone administrator (§19)                                                   */
/* -------------------------------------------------------------------------- */

/**
 * §19's third authority. Assigning it is **account ADMIN only**, deliberately:
 * a zone administrator who could appoint their own successor could hand the
 * zone to somebody the account's ADMINs did not choose, so this control lives
 * here rather than on the Police page.
 *
 * Three states are distinguishable on the wire and all three are reachable
 * here: leave alone (send nothing), appoint (send the id), and clear (send the
 * empty string, after which authority reverts to the account's ADMINs and the
 * pause mail goes to all of them).
 */
function ZoneAdministratorDialog({
  zone,
  users,
  onClose,
}: {
  zone: ZoneSummary | undefined
  users: AccountUser[]
  onClose: () => void
}) {
  const update = useUpdateZone()
  const [selected, setSelected] = useState<string>('')
  const [loadedFor, setLoadedFor] = useState<string | undefined>(undefined)

  if (zone !== undefined && loadedFor !== zone.id) {
    setLoadedFor(zone.id)
    setSelected(zone.administrator_user_id ?? '')
  }

  function close() {
    setLoadedFor(undefined)
    update.reset()
    onClose()
  }

  function submit(event: FormEvent) {
    event.preventDefault()
    if (zone === undefined) return
    // `''` is the clear verb; a UUID appoints. Never send `undefined` here —
    // that means "leave alone", which is not a thing this dialog can want.
    update.mutate({ zoneId: zone.id, body: { administrator_user_id: selected } }, { onSuccess: close })
  }

  const candidates = users.filter((user) => user.user_state === 'ACTIVE')
  const unchanged = selected === (zone?.administrator_user_id ?? '')

  return (
    <Dialog
      open={zone !== undefined}
      onOpenChange={(next) => {
        if (!next) close()
      }}
    >
      <DialogContent className="sm:max-w-lg">
        <form onSubmit={submit}>
          <DialogHeader>
            <DialogTitle>
              Who administers <span className="font-mono">{zone?.name ?? 'this zone'}</span>?
            </DialogTitle>
            <DialogDescription>
              The zone administrator configures its police, pauses and unpauses agents in it, and
              is who pause notifications are emailed to. It is a separate authority from the
              account role: a plain USER can administer a zone, and every account ADMIN can act as
              one for any zone. Only an ADMIN can change this.
            </DialogDescription>
          </DialogHeader>

          <div className="max-h-[22rem] space-y-2 overflow-y-auto py-5">
            {candidates.map((user) => {
              const chosen = selected === user.id
              return (
                <button
                  key={user.id}
                  type="button"
                  aria-pressed={chosen}
                  onClick={() => {
                    setSelected(user.id)
                  }}
                  className={cn(
                    'flex w-full items-center gap-3 rounded-lg border px-3.5 py-2.5 text-left transition-colors',
                    chosen
                      ? 'border-primary bg-[color-mix(in_oklch,var(--primary)_10%,transparent)]'
                      : 'border-border bg-[var(--surface)] hover:border-[color-mix(in_oklch,var(--primary)_40%,var(--border))]',
                  )}
                >
                  <span
                    aria-hidden="true"
                    className={cn(
                      'size-3.5 shrink-0 rounded-full border',
                      chosen ? 'border-primary bg-primary' : 'border-muted-foreground/60',
                    )}
                  />
                  <span className="min-w-0">
                    <span className="block truncate text-sm text-foreground">{user.name}</span>
                    <span className="block truncate font-mono text-xs text-muted-foreground">
                      {user.email}
                    </span>
                  </span>
                  <RoleBadge role={user.role} />
                </button>
              )
            })}

            <button
              type="button"
              aria-pressed={selected === ''}
              onClick={() => {
                setSelected('')
              }}
              className={cn(
                'flex w-full items-center gap-3 rounded-lg border px-3.5 py-2.5 text-left transition-colors',
                selected === ''
                  ? 'border-primary bg-[color-mix(in_oklch,var(--primary)_10%,transparent)]'
                  : 'border-border bg-[var(--surface)] hover:border-[color-mix(in_oklch,var(--primary)_40%,var(--border))]',
              )}
            >
              <span
                aria-hidden="true"
                className={cn(
                  'size-3.5 shrink-0 rounded-full border',
                  selected === '' ? 'border-primary bg-primary' : 'border-muted-foreground/60',
                )}
              />
              <span className="min-w-0">
                <span className="block text-sm text-foreground">Nobody in particular</span>
                <span className="block text-xs text-muted-foreground">
                  Authority reverts to this account&apos;s ADMINs, and so do the pause emails.
                </span>
              </span>
            </button>

            {update.isError ? (
              <Alert variant="destructive">
                <AlertTitle>Could not change the administrator</AlertTitle>
                <AlertDescription><ApiErrorText error={update.error} /></AlertDescription>
              </Alert>
            ) : null}
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={close} disabled={update.isPending}>
              Cancel
            </Button>
            <Button type="submit" disabled={update.isPending || unchanged}>
              {update.isPending ? 'Saving…' : 'Set administrator'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}

/* -------------------------------------------------------------------------- */
/* Lifecycle confirmations                                                    */
/* -------------------------------------------------------------------------- */

interface PendingLifecycle {
  zone: ZoneSummary
  action: ZoneLifecycleAction
}

const LIFECYCLE_COPY: Record<
  ZoneLifecycleAction,
  { title: string; confirm: string; destructive: boolean; body: string }
> = {
  deactivate: {
    title: 'Deactivate',
    confirm: 'Deactivate zone',
    destructive: false,
    body:
      'Every connection currently in the zone is disconnected immediately, and no new connection, channel or message is accepted while it is paused. Everything already written stays readable, and you can reactivate it at any time.',
  },
  activate: {
    title: 'Activate',
    confirm: 'Activate zone',
    destructive: false,
    body:
      'The zone starts accepting connections, channels and messages again. Clients still configured with its MCP URL can reconnect straight away.',
  },
  archive: {
    title: 'Archive',
    confirm: 'Archive permanently',
    destructive: true,
    body:
      'Archiving is permanent. There is no way back to active, and there is no delete: the zone, its connections and its whole message history are kept forever, read-only. Its name also stays reserved, so no future zone on this account can reuse it. Every connection in it is disconnected now.',
  },
}

function LifecycleConfirm({
  pending,
  onClose,
}: {
  pending: PendingLifecycle | undefined
  onClose: () => void
}) {
  const lifecycle = useZoneLifecycle()
  const copy = pending === undefined ? undefined : LIFECYCLE_COPY[pending.action]

  return (
    <AlertDialog
      open={pending !== undefined}
      onOpenChange={(open) => {
        if (!open) {
          lifecycle.reset()
          onClose()
        }
      }}
    >
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>
            {copy?.title ?? 'Change'}{' '}
            <span className="font-mono">{pending?.zone.name ?? 'this zone'}</span>?
          </AlertDialogTitle>
          <AlertDialogDescription>{copy?.body}</AlertDialogDescription>
        </AlertDialogHeader>
        {lifecycle.isError ? (
          <Alert variant="destructive">
            <AlertTitle>That did not work</AlertTitle>
            <AlertDescription><ApiErrorText error={lifecycle.error} /></AlertDescription>
          </Alert>
        ) : null}
        <AlertDialogFooter>
          <AlertDialogCancel disabled={lifecycle.isPending}>Cancel</AlertDialogCancel>
          <AlertDialogAction
            className={cn(
              buttonVariants({ variant: copy?.destructive === true ? 'destructive' : 'default' }),
            )}
            disabled={lifecycle.isPending}
            onClick={(event) => {
              event.preventDefault()
              if (pending === undefined) return
              lifecycle.mutate(
                { zoneId: pending.zone.id, action: pending.action },
                { onSuccess: onClose },
              )
            }}
          >
            {lifecycle.isPending ? 'Working…' : (copy?.confirm ?? 'Confirm')}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  )
}

/* -------------------------------------------------------------------------- */
/* Page                                                                       */
/* -------------------------------------------------------------------------- */

const FILTERS: ReadonlyArray<{ value: ZoneStateFilter; label: string }> = [
  { value: 'active', label: 'Active' },
  { value: 'archived', label: 'Archived' },
  { value: 'all', label: 'All' },
]

export function ZonesPage() {
  const me = useMe()
  const readOnly = useReadOnly()
  const isAdmin = me.data?.role === 'ADMIN'
  const [filter, setFilter] = useState<ZoneStateFilter>('active')
  const zones = useZones(filter)
  const users = useUsers(isAdmin)
  const { activeZone, selectZone } = useActiveZone()

  const [editing, setEditing] = useState<ZoneSummary | undefined>(undefined)
  const [administering, setAdministering] = useState<ZoneSummary | undefined>(undefined)
  const [pending, setPending] = useState<PendingLifecycle | undefined>(undefined)

  const rows = zones.data ?? []
  const userNames = new Map((users.data ?? []).map((user) => [user.id, user.name]))

  /**
   * §19's administrator, for display. A plain USER cannot read `/api/users`, so
   * they see "You" for their own zones and an id for anybody else's — the id is
   * the honest answer, and inventing "someone else" would hide that the seat is
   * held at all.
   */
  function administratorLabel(zone: ZoneSummary): string {
    const id = zone.administrator_user_id
    if (id === null) return 'Unassigned'
    if (id === me.data?.user.id) return 'You'
    return userNames.get(id) ?? id.slice(0, 8)
  }

  return (
    <>
      <PageHeader
        eyebrow="Zones"
        title="Zones"
        description={
          isAdmin
            ? 'A zone is where connections meet: agents, humans and channels all live inside one. Each has its own MCP URL and its own permanent history. Zones are never deleted — they are paused, or archived.'
            : 'The zones you have been granted access to. Each has its own MCP URL and its own message history. Ask an ADMIN on this account for access to more.'
        }
        actions={
          <>
            <Button
              variant="outline"
              onClick={() => {
                void zones.refetch()
              }}
              disabled={zones.isFetching}
            >
              <RefreshCwIcon className={cn('size-4', zones.isFetching ? 'animate-spin' : undefined)} />
              Refresh
            </Button>
            {isAdmin && !readOnly ? <CreateZoneDialog /> : null}
          </>
        }
      />

      <PageBody>
        <div className="flex flex-wrap items-center justify-between gap-4">
          <Tabs
            value={filter}
            onValueChange={(value) => {
              setFilter(value as ZoneStateFilter)
            }}
          >
            <TabsList>
              {FILTERS.map((entry) => (
                <TabsTrigger key={entry.value} value={entry.value}>
                  {entry.label}
                </TabsTrigger>
              ))}
            </TabsList>
          </Tabs>
          <p className="text-sm text-muted-foreground">
            {zones.isLoading ? 'Loading…' : `${rows.length} shown`}
          </p>
        </div>

        {zones.isError ? (
          <ErrorState
            error={zones.error}
            title="Could not load zones"
            onRetry={() => {
              void zones.refetch()
            }}
          />
        ) : zones.isLoading ? (
          <Card className="overflow-hidden">
            <TableSkeleton rows={3} columns={4} />
          </Card>
        ) : rows.length === 0 ? (
          <EmptyState
            icon={LayersIcon}
            title={filter === 'archived' ? 'No archived zones' : 'No zones'}
            description={
              isAdmin
                ? 'Every account starts with a zone named main. Create another whenever you want a separate set of agents that cannot see each other.'
                : 'You have not been granted access to any zone yet. An ADMIN on this account can grant it from the zone members screen.'
            }
          />
        ) : (
          <Card className="overflow-hidden">
            <Table>
              <TableHeader>
                <TableRow className="hover:bg-transparent">
                  <TableHead>Zone</TableHead>
                  <TableHead>State</TableHead>
                  <TableHead>Administrator</TableHead>
                  <TableHead>MCP URL</TableHead>
                  <TableHead>Created</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {rows.map((zone) => {
                  const isActiveSelection = zone.id === activeZone?.id
                  return (
                    <TableRow key={zone.id}>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <span className="font-mono text-sm font-medium text-foreground">
                            {zone.name}
                          </span>
                          {isActiveSelection ? (
                            <Badge variant="outline" className="gap-1">
                              <CheckIcon className="size-3" />
                              selected
                            </Badge>
                          ) : null}
                        </div>
                        <span className="mt-0.5 block max-w-md truncate text-xs text-muted-foreground">
                          {zone.description ?? '—'}
                        </span>
                      </TableCell>
                      <TableCell>
                        <LifecycleBadge state={zone.zone_state} />
                      </TableCell>
                      <TableCell className="text-sm whitespace-nowrap">
                        {zone.administrator_user_id === null ? (
                          <span className="text-muted-foreground">Unassigned</span>
                        ) : (
                          <span className="flex items-center gap-1.5 text-foreground">
                            <ShieldIcon className="size-3.5 text-muted-foreground" />
                            {administratorLabel(zone)}
                          </span>
                        )}
                      </TableCell>
                      <TableCell className="max-w-[22rem]">
                        <CopyableValue value={zone.mcp_url} label={`Copy MCP URL for ${zone.name}`} />
                        <span className="mt-1 block font-mono text-xs text-muted-foreground">
                          {zone.public_id}
                        </span>
                      </TableCell>
                      <TableCell
                        className="text-sm whitespace-nowrap text-muted-foreground"
                        title={formatAbsolute(zone.created_at)}
                      >
                        {formatRelative(zone.created_at)}
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex items-center justify-end gap-1">
                          {/* Archived zones are switchable on purpose: §17 keeps their
                              history readable forever, and this is the only route to it.
                              Every write is gated on `reachable`, which is false for them. */}
                          {isActiveSelection ? null : (
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => {
                                selectZone(zone.id)
                              }}
                            >
                              Switch to
                            </Button>
                          )}
                          {isAdmin && !readOnly ? (
                            <DropdownMenu>
                              <DropdownMenuTrigger asChild>
                                <Button variant="ghost" size="icon-sm" aria-label={`Actions for ${zone.name}`}>
                                  <MoreHorizontalIcon className="size-4" />
                                </Button>
                              </DropdownMenuTrigger>
                              <DropdownMenuContent align="end" className="w-[13rem]">
                                <DropdownMenuItem
                                  disabled={zone.zone_state === 'ARCHIVED'}
                                  onSelect={() => {
                                    setEditing(zone)
                                  }}
                                >
                                  <PencilIcon className="size-3.5" />
                                  Edit zone
                                </DropdownMenuItem>
                                <DropdownMenuItem
                                  disabled={zone.zone_state === 'ARCHIVED'}
                                  onSelect={() => {
                                    setAdministering(zone)
                                  }}
                                >
                                  <ShieldIcon className="size-3.5" />
                                  Set administrator
                                </DropdownMenuItem>
                                <DropdownMenuItem asChild>
                                  <Link to={`/app/zones/${zone.id}/members`}>
                                    <UsersIcon className="size-3.5" />
                                    Manage access
                                  </Link>
                                </DropdownMenuItem>
                                <DropdownMenuSeparator />
                                {zone.zone_state === 'ACTIVE' ? (
                                  <DropdownMenuItem
                                    onSelect={() => {
                                      setPending({ zone, action: 'deactivate' })
                                    }}
                                  >
                                    <PauseIcon className="size-3.5" />
                                    Deactivate
                                  </DropdownMenuItem>
                                ) : null}
                                {zone.zone_state === 'DEACTIVATED' ? (
                                  <DropdownMenuItem
                                    onSelect={() => {
                                      setPending({ zone, action: 'activate' })
                                    }}
                                  >
                                    <PlayIcon className="size-3.5" />
                                    Activate
                                  </DropdownMenuItem>
                                ) : null}
                                {zone.zone_state === 'ARCHIVED' ? null : (
                                  <DropdownMenuItem
                                    className="text-destructive [&_svg]:text-destructive"
                                    onSelect={() => {
                                      setPending({ zone, action: 'archive' })
                                    }}
                                  >
                                    <ArchiveIcon className="size-3.5" />
                                    Archive permanently
                                  </DropdownMenuItem>
                                )}
                              </DropdownMenuContent>
                            </DropdownMenu>
                          ) : null}
                        </div>
                      </TableCell>
                    </TableRow>
                  )
                })}
              </TableBody>
            </Table>
          </Card>
        )}

        <p className="text-xs text-muted-foreground">
          There is no delete, at any layer. Messages are permanent, and deleting a zone would
          orphan them — so a zone you are finished with is archived instead.
        </p>
      </PageBody>

      <EditZoneDialog
        zone={editing}
        onClose={() => {
          setEditing(undefined)
        }}
      />
      <ZoneAdministratorDialog
        zone={administering}
        users={users.data ?? []}
        onClose={() => {
          setAdministering(undefined)
        }}
      />
      <LifecycleConfirm
        pending={pending}
        onClose={() => {
          setPending(undefined)
        }}
      />
    </>
  )
}
